import type {SubFlowType} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel.types';
import type {DigitalSignageProviderSubFlow} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';
import type {BasePanel} from '@Components/panel/panel.types';
import type {OrderDetailsForRedux} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import type {SocialMediaResponse, WebLinkResponse} from '@Panels/poster-publishing-options-panel/components/publishing-social-media-panel/publishing-social-media-panel.types';
import type {PublishingOptionsList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list.types';
import type {PageHashedId} from '@Components/social-media/publish-params.types';

export enum DisplayType {
  MODAL = 'MODAL',
  DROPDOWN = 'DROPDOWN',
}

export enum ThumbNailType {
  IMAGE = 'IMAGE',
  ICON = 'ICON',
}

export const PUBLISHING_OPTIONS_PANEL_ID = 'PUBLISHING_OPTIONS_PANEL_ID';

export enum PublishingAnalyticsAction {
  DOWNLOAD = 'ChooseDownloadType',
  PUBLISH = 'ChoosePublishType',
  SIGNAGE = 'ChooseSignageType',
  SOCIAL = 'ChooseSocialType',
  SIGNAGE_PUBLISH = 'DigitalSignagePublish',
  SHARE_HOSTED_LINK_PUBLISH = 'ShareHostedLinkPublished',
  SHARE_HOSTED_LINK_UNPUBLISH = 'ShareHostedLinkUnpublished',
}

export enum SelectionType {
  DEFAULT_DISPLAY = 'DEFAULT_DISPLAY',
  POST_TO_SOCIAL_MEDIA = 'POST_TO_SOCIAL_MEDIA',
  DIGITAL_SIGNAGE_DISPLAY = 'DIGITAL_SIGNAGE_DISPLAY',
  PUBLISH_WEBPAGE_DISPLAY = 'PUBLISH_WEBPAGE_DISPLAY',
  EMBED_WEBPAGE_DISPLAY = 'EMBED_WEBPAGE_DISPLAY',
  SOCIAL_MEDIA_LINK = 'SOCIAL_MEDIA_LINK',
  SUB_DISPLAY = 'SUB_DISPLAY',
  SHARE_TO_APP = 'SHARE_TO_APP',
  QR_CODE = 'QR_CODE',
  CONFIRMATION_DISPLAY = 'CONFIRMATION_DISPLAY',
  PUBLISH_EMAIL_CAMPAIGN = 'PUBLISH_EMAIL_CAMPAIGN',
  CREATE_EVENT_WEBPAGE = 'CREATE_EVENT_WEBPAGE',
}

export enum Status {
  DOWNLOADABLE = 'downloadable',
  NEEDS_ATTENTION = 'needs-attention',
  NEW_ORDER = 'new-order',
  UNPURCHASED = 'unpurchased',
  FREEBIE = 'freebie',
  OUTDATED = 'outdated',
  OUT_OF_FREE_DOWNLOADS = 'out-of-free-downloads',
  GENERATING = 'generating',
  CREDIT_USABLE = 'credit-usable',
  UNDEFINED = 'undefined',
}

export enum Mode {
  RAYDIANT = 'raydiant',
  SIGNAGELIVE = 'signagelive',
  SCREENCLOUD = 'screencloud',
  DIGITAL_SIGNAGE = 'digital-signage',
  EMBED_LINKS = 'embed-links',
  DOWNLOAD = 'download',
}

export enum PublishingPurchaseErrorStatus {
  PERMISSION_DENIED = 'permission_denied',
  GETTY_REQUIREMENTS_NOT_MET = 'getty_requirements_not_met',
  ORDER_NOT_FOUND = 'order_not_found',
  PURCHASE_NOT_FOUND = 'purchase_not_found',
  PARAM_MISSING = 'param_missing',
  QUEUE_ERROR = 'queue_error',
}

export enum PublishAndDownloadMode {
  DOWNLOAD = 'download',
  PUBLISH = 'publish',
}

export interface CheckoutDownloadProps {
  idposter: string;
  idproduct: number;
  promotion: string;
  mode: PublishAndDownloadMode;
  phids: string;
}

export enum LoadingSleepTime {
  DEFAULT = 3,
  SMALL = 5,
  HIGH = 10,
}

export interface PosterPublishingOptionsPanelProps extends BasePanel {
  isPosterMaker: number;
  preselectPaidProduct: boolean;
  isPremium: number;
  posterType?: string;
  idGalleryTemplate?: string;
  idTemplate?: string;
  uc: number;
  isVideo: number;
  hasVideoItem: number;
  isExpensiveVideo: number;
  idPosterHash?: string;
  isTransparent: number;
  hasClickableLink: number;
  pw?: number;
  ph?: number;
  ptw?: number;
  pth?: number;
  ptu?: string;
  panelDetails: PosterPublishingOptionsPanelSubProps;
  defaultPanelFlowState?: SelectionType;
  recommendedOptions?: PublishingOptionsList[];
  onSuccessfulConfirmation?: () => void;
}

interface PosterPublishingOptionsPanelSubProps {
  displayType: DisplayType;
  onClose?: VoidFunction;
}

export interface PosterProductDataForProcessDesign {
  id: number;
  name: string;
  description: string;
  rawPrice: number;
  price: string;
  productPrice: number;
  daysFree: number;
  premiumContentAdded: boolean;
  retriesLeft: number;
  creditsLeft: number;
  productCreditPrice: number;
  extraInfo: [];
  galleryTemplateHash: string | null;
  status: Status;
  orderId: string | null;
  purchaseId: string | null;
  encryptedPurchaseId: string;
  isWebDownload: boolean;
  orderFileURLs: [];
  downloadUrl: string;
  downloadFileUrl: string;
  isFree: boolean;
  hashedId: string;
  posterId: string;
  isVideo: boolean;
  remainingCredits: number;
  format: string;
  maxDimension: string;
  height: number;
  width: number;
  hasWatermark: boolean;
  hasClickableLinks: boolean;
  isLargeSize: boolean;
  posterRenderId: string;
  iconClassName: string;
  needsPurchase: boolean;
  posterName: string;
  mimeType: string;
  needsRegenerationDueToDeletion: boolean | null;
  existingPageOrders?: Record<PageHashedId, string>;
}

interface DigitalSignageProviderCounts {
  [SubFlowType.RAYDIANT_SUBDISPLAY]?: number;
  [SubFlowType.SMART_TV_SUBDISPLAY]?: number;
  [SubFlowType.SCREEN_CLOUD_SUBDISPLAY]?: number;
  [SubFlowType.SIGNAGELIVE_WIDGET_SUBDISPLAY]?: number;
  [SubFlowType.POSTERBOOKING_SUBDISPLAY]?: number;
  [SubFlowType.FUGO_SUBDISPLAY]?: number;
  [SubFlowType.YODECK_SUBDISPLAY]?: number;
  [SubFlowType.VIZROM_SUBDISPLAY]?: number;
  [SubFlowType.DRIPBOARDS_SUBDISPLAY]?: number;
  [SubFlowType.CLEAR_DIGITAL_SHO_SUBDISPLAY]?: number;
}

/**
 * Per-option click counts powering the Recommended section (stored in user_meta.publishing_option_stats).
 * Every field is optional — a user has a count only for options they have used — and DIGITAL_SIGNAGE nests its per-provider counts.
 */
export interface PublishingOptionStats {
  [PublishingOptionsList.SOCIAL_MEDIA]?: number;
  [PublishingOptionsList.WEB_LINK]?: number;
  [PublishingOptionsList.EMAIL_CAMPAIGN]?: number;
  [PublishingOptionsList.DOWNLOADED_POSTER]?: number;
  [PublishingOptionsList.EMBED_IN_WEBPAGE]?: number;
  [PublishingOptionsList.SHARE_ON_SOCIAL_MEDIA]?: number;
  [PublishingOptionsList.SHARE_TO_APP]?: number;
  [PublishingOptionsList.QR_CODE]?: number;
  [PublishingOptionsList.PROMOTE_AS_EVENT]?: number;
  [PublishingOptionsList.INVITE]?: number;
  [PublishingOptionsList.DIGITAL_SIGNAGE]?: DigitalSignageProviderCounts;
}

export interface AJAXResponseProductDataComplete {
  freebieProduct: PosterProductDataForProcessDesign;
  productData: PosterProductDataForProcessDesign[];
  socialMediaLinks: SocialMediaResponse;
  webLinks: WebLinkResponse;
  previewUrl: string;
}

export enum ProductType {
  PRINT = 0,
  POSTER = 1,
  FREEBIE = 2,
  DEFAULT = 3,
}

export interface PosterPublishingOptionsReduxState {
  displayType: DisplayType;
  selectionType: SelectionType;
  subFlowType: SubFlowType;
  orderDetails: OrderDetailsForRedux;
  freebieProductData: PosterProductDataForProcessDesign;
  premiumProductDataForPrint: PosterProductDataForProcessDesign;
  premiumProductDataForPoster?: PosterProductDataForProcessDesign;
  productType?: ProductType;
  showLoader: boolean;
  showError: boolean;
  errorMessage: string;
  socialMediaLinks: SocialMediaResponse;
  webLinks: WebLinkResponse;
  hashedId: string;
  posterName: string;
  posterPreviewUrl: string;
  posterPageHashedId: string;
  publishingOptionStats: PublishingOptionStats | null;
  pendingSignageSubFlow: DigitalSignageProviderSubFlow | null;
}

export const PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING = 0;
